import { notifications } from "@mantine/notifications";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  type BulkReleaseActionRequest,
  type BulkReleaseActionResponse,
  type DeleteReleaseResponse,
  type PaginatedReleases,
  type PollAllNowResponse,
  type ReleaseFacets,
  type ReleaseFacetsParams,
  type ReleaseInboxParams,
  type ReleaseLedgerEntry,
  type ReleaseSource,
  type ResetAllReleaseSourcesResponse,
  type ResetReleaseSourceResponse,
  releaseSourcesApi,
  releasesApi,
  type SeriesReleaseListParams,
  type UpdateReleaseLedgerEntryRequest,
  type UpdateReleaseSourceRequest,
} from "@/api/releases";
import { useTaskProgress } from "@/hooks/useTaskProgress";

const RELEASE_POLL_TASK_TYPE = "poll_release_source";

export const releasesKeys = {
  inbox: (params: ReleaseInboxParams) => ["releases", "inbox", params] as const,
  facets: (params: ReleaseFacetsParams) =>
    ["releases", "facets", params] as const,
  series: (seriesId: string, params: SeriesReleaseListParams) =>
    ["series", seriesId, "releases", params] as const,
  inboxRoot: ["releases", "inbox"] as const,
  sourcesRoot: ["release-sources"] as const,
};

/**
 * Refresh the per-series release views touched by a release-state change.
 *
 * Release ledger panels are keyed `["series", id, "releases", …]`, which do NOT
 * start with `["releases"]`, so the inbox/facets invalidation misses them. The
 * previous code reached them with a bare `["series"]` invalidation — but that
 * prefix-matches *every* series query (full, metadata, covers, tracking,
 * aliases, and all the lists/home sections), none of which a release-state
 * change touches (the series DTO carries no release-derived fields). With
 * several detail tabs open, one dismiss/acquire became a full-namespace
 * refetch. This targets only the release ledger panels (and, for ledger-wiping
 * resets, the tracking row whose latest_known high-water mark can move).
 */
function invalidateSeriesReleaseViews(
  queryClient: QueryClient,
  opts?: { tracking?: boolean },
) {
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "series" &&
      (query.queryKey[2] === "releases" ||
        (opts?.tracking === true && query.queryKey[2] === "tracking")),
  });
}

export function useReleaseFacets(params: ReleaseFacetsParams = {}) {
  return useQuery<ReleaseFacets>({
    queryKey: releasesKeys.facets(params),
    queryFn: () => releasesApi.facets(params),
  });
}

export function useDeleteRelease() {
  const queryClient = useQueryClient();
  return useMutation<DeleteReleaseResponse, Error, string>({
    mutationFn: (releaseId) => releasesApi.delete(releaseId),
    onSuccess: () => {
      // Delete touches the ledger and (server-side) the source's etag.
      // Invalidate both so the table and the source-admin row refresh.
      queryClient.invalidateQueries({ queryKey: ["releases"] });
      invalidateSeriesReleaseViews(queryClient);
      queryClient.invalidateQueries({ queryKey: releasesKeys.sourcesRoot });
    },
    onError: notifyError("Failed to delete release"),
  });
}

export function useBulkReleaseAction() {
  const queryClient = useQueryClient();
  return useMutation<
    BulkReleaseActionResponse,
    Error,
    BulkReleaseActionRequest
  >({
    mutationFn: (request) => releasesApi.bulk(request),
    onSuccess: (data) => {
      const { affected, action } = data;
      const verb =
        action === "dismiss"
          ? "Dismissed"
          : action === "mark-acquired"
            ? "Marked acquired"
            : action === "ignore"
              ? "Ignored"
              : action === "reset"
                ? "Reset"
                : "Deleted";
      const noun = affected === 1 ? "release" : "releases";
      notifications.show({
        title: `${verb} ${affected} ${noun}`,
        // Surface the etag-clear side effect for delete so the user knows
        // the row will come back on the next poll.
        message:
          action === "delete"
            ? "Affected sources will re-fetch on the next poll."
            : undefined,
        color: action === "delete" ? "orange" : "blue",
      });
      queryClient.invalidateQueries({ queryKey: ["releases"] });
      invalidateSeriesReleaseViews(queryClient);
      if (action === "delete") {
        queryClient.invalidateQueries({ queryKey: releasesKeys.sourcesRoot });
      }
    },
    onError: notifyError("Bulk action failed"),
  });
}

export function useReleaseInbox(params: ReleaseInboxParams = {}) {
  return useQuery<PaginatedReleases>({
    queryKey: releasesKeys.inbox(params),
    queryFn: () => releasesApi.listInbox(params),
  });
}

export function useSeriesReleases(
  seriesId: string,
  params: SeriesReleaseListParams = {},
  enabled = true,
) {
  return useQuery<PaginatedReleases>({
    queryKey: releasesKeys.series(seriesId, params),
    queryFn: () => releasesApi.listForSeries(seriesId, params),
    enabled: enabled && Boolean(seriesId),
  });
}

function notifyError(title: string) {
  return (error: Error & { response?: { data?: { error?: string } } }) => {
    notifications.show({
      title,
      message: error.response?.data?.error || error.message || "Unknown error",
      color: "red",
    });
  };
}

export function useDismissRelease() {
  const queryClient = useQueryClient();
  return useMutation<ReleaseLedgerEntry, Error, string>({
    mutationFn: (releaseId) => releasesApi.dismiss(releaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["releases"] });
      invalidateSeriesReleaseViews(queryClient);
    },
    onError: notifyError("Failed to dismiss release"),
  });
}

export function useMarkReleaseAcquired() {
  const queryClient = useQueryClient();
  return useMutation<ReleaseLedgerEntry, Error, string>({
    mutationFn: (releaseId) => releasesApi.markAcquired(releaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["releases"] });
      invalidateSeriesReleaseViews(queryClient);
    },
    onError: notifyError("Failed to mark release acquired"),
  });
}

export function usePatchRelease() {
  const queryClient = useQueryClient();
  return useMutation<
    ReleaseLedgerEntry,
    Error,
    { releaseId: string; update: UpdateReleaseLedgerEntryRequest }
  >({
    mutationFn: ({ releaseId, update }) =>
      releasesApi.patchEntry(releaseId, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["releases"] });
      invalidateSeriesReleaseViews(queryClient);
    },
    onError: notifyError("Failed to update release"),
  });
}

export function useReleaseSources() {
  const queryClient = useQueryClient();
  const { activeTasks } = useTaskProgress();

  const query = useQuery<ReleaseSource[]>({
    queryKey: releasesKeys.sourcesRoot,
    queryFn: () => releaseSourcesApi.list(),
    // Belt-and-braces: SSE `release_source_polled` invalidates this query, but
    // very fast polls can race the event pipeline. While any release-poll task
    // is in flight, refetch every 5s so `lastPolledAt` / `lastSummary` /
    // `lastError` catch up even if the event is missed. Stops once no polls
    // are active.
    refetchInterval: () => {
      const hasActivePoll = Array.from(activeTasks.values()).some(
        (task) =>
          task.taskType === RELEASE_POLL_TASK_TYPE &&
          (task.status === "pending" || task.status === "running"),
      );
      return hasActivePoll ? 5000 : false;
    },
  });

  // Refresh immediately when a release-poll task transitions to a terminal
  // state. `useTaskProgress` keeps completed/failed entries around briefly,
  // so we watch for the status flip rather than disappearance.
  const prevStatusesRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const prev = prevStatusesRef.current;
    const next = new Map<string, string>();

    for (const task of activeTasks.values()) {
      if (task.taskType !== RELEASE_POLL_TASK_TYPE) continue;
      next.set(task.taskId, task.status);

      const prevStatus = prev.get(task.taskId);
      if (
        prevStatus &&
        prevStatus !== task.status &&
        (task.status === "completed" || task.status === "failed")
      ) {
        queryClient.invalidateQueries({ queryKey: releasesKeys.sourcesRoot });
      }
    }

    prevStatusesRef.current = next;
  }, [activeTasks, queryClient]);

  return query;
}

export function useUpdateReleaseSource() {
  const queryClient = useQueryClient();
  return useMutation<
    ReleaseSource,
    Error,
    { sourceId: string; update: UpdateReleaseSourceRequest }
  >({
    mutationFn: ({ sourceId, update }) =>
      releaseSourcesApi.update(sourceId, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: releasesKeys.sourcesRoot });
    },
    onError: notifyError("Failed to update source"),
  });
}

export function usePollReleaseSourceNow() {
  const queryClient = useQueryClient();
  return useMutation<{ status: string; message: string }, Error, string>({
    mutationFn: (sourceId) => releaseSourcesApi.pollNow(sourceId),
    onSuccess: () => {
      notifications.show({
        title: "Poll enqueued",
        message: "The release source will be polled shortly.",
        color: "blue",
      });
      queryClient.invalidateQueries({ queryKey: releasesKeys.sourcesRoot });
    },
    onError: notifyError("Failed to enqueue poll"),
  });
}

/**
 * Fan out a poll request across every enabled release source. Reports a
 * single summary toast once the server has finished enqueueing — this
 * does *not* wait for the polls themselves to run.
 */
export function usePollAllReleaseSourcesNow() {
  const queryClient = useQueryClient();
  return useMutation<PollAllNowResponse, Error, void>({
    mutationFn: () => releaseSourcesApi.pollNowAll(),
    onSuccess: (data) => {
      const skipped = data.considered - data.enqueued - data.coalesced;
      const parts = [
        `${data.enqueued} enqueued`,
        data.coalesced > 0 ? `${data.coalesced} already running` : null,
        data.failed > 0 ? `${data.failed} failed (see logs)` : null,
        skipped > 0 ? `${skipped} skipped` : null,
      ].filter(Boolean);
      notifications.show({
        title: "Polling all sources",
        message: parts.join(" · "),
        color: data.failed > 0 ? "yellow" : "blue",
      });
      queryClient.invalidateQueries({ queryKey: releasesKeys.sourcesRoot });
    },
    onError: notifyError("Failed to enqueue polls"),
  });
}

export function useResetReleaseSource() {
  const queryClient = useQueryClient();
  return useMutation<ResetReleaseSourceResponse, Error, string>({
    mutationFn: (sourceId) => releaseSourcesApi.reset(sourceId),
    onSuccess: (data) => {
      notifications.show({
        title: "Source reset",
        message: `Cleared ${data.deletedLedgerEntries} ledger ${
          data.deletedLedgerEntries === 1 ? "entry" : "entries"
        }. Click "Poll now" to re-fetch.`,
        color: "blue",
      });
      // Reset wipes ledger rows, so invalidate everything that reads them.
      queryClient.invalidateQueries({ queryKey: releasesKeys.sourcesRoot });
      queryClient.invalidateQueries({ queryKey: releasesKeys.inboxRoot });
      invalidateSeriesReleaseViews(queryClient, { tracking: true });
    },
    onError: notifyError("Failed to reset source"),
  });
}

/**
 * Reset every release source. Destructive — caller is responsible for
 * confirming with the user before invoking the mutation.
 */
export function useResetAllReleaseSources() {
  const queryClient = useQueryClient();
  return useMutation<ResetAllReleaseSourcesResponse, Error, void>({
    mutationFn: () => releaseSourcesApi.resetAll(),
    onSuccess: (data) => {
      const parts = [
        `${data.reset} of ${data.considered} sources reset`,
        `${data.deletedLedgerEntries} ledger ${
          data.deletedLedgerEntries === 1 ? "entry" : "entries"
        } removed`,
        data.failed > 0 ? `${data.failed} failed (see logs)` : null,
      ].filter(Boolean);
      notifications.show({
        title: "All sources reset",
        message: parts.join(" · "),
        color: data.failed > 0 ? "yellow" : "blue",
      });
      // Same invalidations as the per-source reset; the blast radius
      // covers everything that reads ledger rows.
      queryClient.invalidateQueries({ queryKey: releasesKeys.sourcesRoot });
      queryClient.invalidateQueries({ queryKey: releasesKeys.inboxRoot });
      invalidateSeriesReleaseViews(queryClient, { tracking: true });
    },
    onError: notifyError("Failed to reset sources"),
  });
}
